import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, 
  Users, 
  Trophy, 
  Briefcase, 
  MessageSquare,
  BarChart3,
  TrendingUp,
  FileText,
  ArrowRight,
  Sparkles,
  MapPin
} from 'lucide-react';

function Reports() {
  const navigate = useNavigate();

  const reports = [
    {
      id: 'branch',
      title: 'Branch Performance',
      description: 'Comprehensive branch analytics including sales metrics, performance comparisons, and branch-specific insights.',
      icon: Building2,
      color: 'blue',
      path: '/reports/branch',
      features: ['Sales metrics', 'Performance comparison', 'Branch analytics']
    },
    {
      id: 'customer-analysis',
      title: 'Customer Analysis',
      description: 'Deep dive into customer behavior, spending patterns, rebate tracking, and voucher performance.',
      icon: Users,
      color: 'purple',
      path: '/reports/customer-analysis',
      features: ['Customer spending', 'Rebate tracking', 'Voucher analysis']
    },
    {
      id: 'advisor',
      title: 'Advisor Performance',
      description: 'Track advisor sales performance, margins, and customer relationships with detailed metrics.',
      icon: Trophy,
      color: 'orange',
      path: '/reports/advisor',
      features: ['Sales performance', 'Margin analysis', 'Customer metrics']
    },
    {
      id: 'bdm',
      title: 'BDM Activity',
      description: 'Monitor BDM visit activity, notes, and engagement metrics across all customers.',
      icon: Briefcase,
      color: 'green',
      path: '/reports/bdm',
      features: ['Visit tracking', 'Activity notes', 'Engagement metrics']
    },
    {
      id: 'customer-contacts',
      title: 'Customer Contacts',
      description: 'View and manage all customer messages, interactions, and communication history.',
      icon: MessageSquare,
      color: 'indigo',
      path: '/reports/customer-contacts',
      features: ['Message history', 'Contact tracking', 'Interaction logs']
    },
    {
      id: 'sales-analysis',
      title: 'Sales Analysis',
      description: 'Comprehensive sales reports including invoice summaries, area analysis, weekly and daily sales breakdowns.',
      icon: FileText,
      color: 'blue',
      path: '/reports/sales-analysis',
      features: ['Invoice summaries', 'Area analysis', 'Daily/Weekly breakdowns']
    },
    {
      id: 'area-comparison',
      title: 'Area Comparison',
      description: 'Compare sales performance across all areas with customizable time periods (week, month, quarter, year).',
      icon: MapPin,
      color: 'green',
      path: '/reports/area-comparison',
      features: ['Period comparison', 'Area analytics', 'Growth metrics']
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: {
        bg: 'bg-blue-100',
        text: 'text-blue-600',
        hover: 'hover:bg-blue-50',
        border: 'border-blue-200',
        button: 'bg-blue-600 hover:bg-blue-700'
      },
      purple: {
        bg: 'bg-purple-100',
        text: 'text-purple-600',
        hover: 'hover:bg-purple-50',
        border: 'border-purple-200',
        button: 'bg-purple-600 hover:bg-purple-700'
      },
      orange: {
        bg: 'bg-orange-100',
        text: 'text-orange-600',
        hover: 'hover:bg-orange-50',
        border: 'border-orange-200',
        button: 'bg-orange-600 hover:bg-orange-700'
      },
      green: {
        bg: 'bg-green-100',
        text: 'text-green-600',
        hover: 'hover:bg-green-50',
        border: 'border-green-200',
        button: 'bg-green-600 hover:bg-green-700'
      },
      indigo: {
        bg: 'bg-indigo-100',
        text: 'text-indigo-600',
        hover: 'hover:bg-indigo-50',
        border: 'border-indigo-200',
        button: 'bg-indigo-600 hover:bg-indigo-700'
      }
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header Section */}
        <div className="mb-12">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-3 bg-indigo-100 rounded-lg">
              <BarChart3 className="w-8 h-8 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Advanced Reports</h1>
              <p className="text-lg text-gray-600 mt-2">
                Comprehensive analytics and insights for the VCP Management Team
              </p>
            </div>
          </div>
          
          <div className="mt-6 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-6 border border-indigo-200">
            <div className="flex items-start space-x-4">
              <Sparkles className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-2">What You Can Do</h2>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center space-x-2">
                    <TrendingUp className="w-4 h-4 text-indigo-600" />
                    <span>Track performance metrics across branches, advisors, and BDMs</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Users className="w-4 h-4 text-indigo-600" />
                    <span>Analyse customer behavior, spending patterns, and engagement</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <FileText className="w-4 h-4 text-indigo-600" />
                    <span>Export reports to PDF or Excel for sharing and analysis</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <MessageSquare className="w-4 h-4 text-indigo-600" />
                    <span>Monitor customer communications and interaction history</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Reports Grid */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Available Reports</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reports.map((report) => {
              const colors = getColorClasses(report.color);
              const Icon = report.icon;
              
              return (
                <div
                  key={report.id}
                  className={`bg-white rounded-xl shadow-sm border ${colors.border} ${colors.hover} transition-all duration-200 hover:shadow-lg cursor-pointer group`}
                  onClick={() => navigate(report.path)}
                >
                  <div className="p-6">
                    {/* Icon and Title */}
                    <div className="flex items-start justify-between mb-4">
                      <div className={`p-3 ${colors.bg} rounded-lg`}>
                        <Icon className={`w-6 h-6 ${colors.text}`} />
                      </div>
                      <ArrowRight className={`w-5 h-5 ${colors.text} opacity-0 group-hover:opacity-100 transition-opacity`} />
                    </div>
                    
                    {/* Content */}
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {report.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                      {report.description}
                    </p>
                    
                    {/* Features */}
                    <div className="space-y-1 mb-4">
                      {report.features.map((feature, index) => (
                        <div key={index} className="flex items-center space-x-2 text-sm text-gray-500">
                          <div className={`w-1.5 h-1.5 rounded-full ${colors.bg}`} />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(report.path);
                      }}
                      className={`w-full ${colors.button} text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2`}
                    >
                      <span>View Report</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick Stats Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Report Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <BarChart3 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Interactive Charts</h3>
                <p className="text-sm text-gray-600">
                  Visualize data with interactive charts and graphs that update in real-time
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FileText className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Export Options</h3>
                <p className="text-sm text-gray-600">
                  Export reports to PDF or Excel format for sharing and offline analysis
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Real-time Data</h3>
                <p className="text-sm text-gray-600">
                  All reports pull live data from your system, ensuring accuracy and timeliness
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-8 bg-gray-50 rounded-xl p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Need Help?</h3>
          <p className="text-gray-600 text-sm">
            Each report includes filtering options, export capabilities, and detailed metrics. 
            Click on any report card above to explore its features. Use the filters to customize 
            your view and export data for further analysis.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Reports;
